import { isDefined, isNotDefined } from '@sgme/fp';
import { HStack, VStack } from '@sgme/ui';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import clsx from 'clsx';
import deepEqual from 'fast-deep-equal';
import { FormattedMessage } from 'react-intl';

import { ErrorRates } from '@/App/tiles/shared/ErrorRates.tsx';
import { ForbiddenExecutionTooltip } from '@/App/tiles/shared/ForbiddenExecutionTooltip.tsx';
import { SaleTier } from '@/App/tiles/shared/SaleTier.tsx';
import { FormattedPrice } from '@/components/ui/FormattedPrice.tsx';
import { checkIfUserCanTrade, checkIfUserInternalSales, useUserContext } from '@/context/UserContext.tsx';
import { useAppSelector } from '@/store/hooks.ts';
import { getSignalRConnectionId } from '@/store/streaming/slice';
import { useExecuteSwapRfsMutation } from '@/store/swap/api/index.ts';
import { isSwapPredeliver, isSwapRollover } from '@/store/swap/helpers/tile.ts';
import { buildSwapExecutionPrices } from '@/store/swap/selectors/executionPrices.ts';
import { selectSwapPrices } from '@/store/swap/selectors/rfs.ts';
import { selectSwapTile } from '@/store/swap/selectors/tile.ts';
import { selectTileClientId } from '@/store/tiles/selectors.ts';
import { convertBidAskPointToBidAskPPS } from '@/utils/number-point.ts';
import { getRateComponentStyling } from '@/utils/rateComponentStyling.ts';

import '../CashTile/shared/RatePanel.css';

export type SwapRatesProps = {
  tileId: string;
};

export const SwapRates = (props: SwapRatesProps) => {
  const { tileId } = props;

  const { sgwtWebAnalytics } = useSgwtWidgets();
  const userContextValue = useUserContext();

  const userPreferences = useAppSelector((state) => state.userPreferences);
  const { showColoredPrices } = userPreferences;
  const preferedEmails = userPreferences.splitNotifications
    ? userPreferences.splitNotificationsEmailsCash
    : userPreferences.emails;

  const notificationEmail = preferedEmails.length > 0 ? preferedEmails.join(';') : userContextValue.currentUser.login;

  const { tile, clientId, prices, signalRConnectionId, rateError } = useAppSelector((state) => {
    const tile = selectSwapTile(state, tileId);

    if (isNotDefined(tile) || tile.type !== 'FxSwap') {
      return {};
    }

    const clientId = selectTileClientId(state, tileId);
    const signalRConnectionId = getSignalRConnectionId(state);

    if (isDefined(tile.properties.error) && tile.properties.error.type === 'rate') {
      return {
        tile,
        clientId,
        prices: undefined,
        signalRConnectionId,
        notificationEmail,
        rateError: tile.properties.error,
      };
    }

    const prices = selectSwapPrices(state, tileId);

    return {
      tile,
      clientId,
      prices,
      signalRConnectionId,
      notificationEmail,
      rateError: undefined,
    };
  }, deepEqual);

  const isSaleUser = checkIfUserInternalSales(userContextValue);
  const canTrade = checkIfUserCanTrade(userContextValue);

  const [executeSwapRfs] = useExecuteSwapRfsMutation({ fixedCacheKey: prices?.rfsPrice.rfsId });

  if (isDefined(rateError)) {
    return <ErrorRates code={rateError.code} message={rateError.message} />;
  }

  if (isNotDefined(tile) || isNotDefined(prices) || tile.tradeCapture.status === 'CHECKING') {
    return (
      <HStack
        fullWidth
        className="position-relative border-top border-bottom"
        style={{ height: 100 }}
        data-e2e="empty-rates"
      >
        <div className="w-50 h-100 border-end text-center fw-semibold" style={{ height: 100 }}>
          <VStack
            horizontalAlignment="center"
            verticalAlignment="center"
            fullWidth
            fullHeight
            className={clsx('px-2 py-1', ...getRateComponentStyling({ executable: false, cantTrade: !canTrade }))}
          >
            <div className="text-secondary">
              <FormattedMessage id="SwapTile.Rates.FillInDataBid" values={{ br: <br /> }} />
            </div>
          </VStack>
        </div>

        <div className="w-50 h-100 text-center fw-semibold" style={{ height: 100 }}>
          <VStack
            horizontalAlignment="center"
            verticalAlignment="center"
            fullWidth
            fullHeight
            className={clsx('px-2 py-1', ...getRateComponentStyling({ executable: false, cantTrade: !canTrade }))}
          >
            <div className="text-secondary">
              <FormattedMessage id="SwapTile.Rates.FillInDataAsk" values={{ br: <br /> }} />
            </div>
          </VStack>
        </div>
      </HStack>
    );
  }

  const { rates, commercialMarginSwapPoints, commercialMarginDefaultSwapPoints, isSaleTier } = prices;

  const currency =
    tile.properties.nearNotionalCurrencyIndex === 1 ? tile.properties.currency1 : tile.properties.currency2;

  const bidMessageId = isSaleUser
    ? tile.properties.nearNotionalCurrencyIndex === 1
      ? 'SwapTile.Rates.clientBuys'
      : 'SwapTile.Rates.clientSells'
    : tile.properties.nearNotionalCurrencyIndex === 1
      ? 'SwapTile.Rates.youBuy'
      : 'SwapTile.Rates.youSell';

  const askMessageId = isSaleUser
    ? tile.properties.nearNotionalCurrencyIndex === 1
      ? 'SwapTile.Rates.clientSells'
      : 'SwapTile.Rates.clientBuys'
    : tile.properties.nearNotionalCurrencyIndex === 1
      ? 'SwapTile.Rates.youSell'
      : 'SwapTile.Rates.youBuy';

  const appliedMarginSwapPPS = convertBidAskPointToBidAskPPS(
    commercialMarginSwapPoints ?? commercialMarginDefaultSwapPoints,
  );

  // which digits will be emphasized when displaying the rates ?
  // formula are translations of definitions at https://jira.world.socgen/browse/SGEFX-1033
  // due to preliminary conversion from points to pps (x->x/10) this computation could be reduced
  // but it is kept as is to stick to the specs

  // example:
  //
  // floor(log(swap margin PPS)) => highlight digit position
  //  2 => -2  ( 100    <= swap margin pps < 1000  ) : highlight up to (and including) tens
  //  1 => -1  (  10    <= swap margin pps <  100  ) : highlight up to (and including) units
  //  0 =>  0  (   1    <= swap margin pps <   10  ) : highlight up to (and including) first  decimal
  // -1 =>  1  (   0.1  <= swap margin pps <    1  ) : highlight up to (and including) second decimal
  // -1 =>  2  (   0.01 <= swap margin pps <    0.1) : highlight up to (and including) third  decimal
  const highlightDigitPositions = {
    bid: -Math.floor(Math.log10(Math.abs(appliedMarginSwapPPS.bid))),
    ask: -Math.floor(Math.log10(Math.abs(appliedMarginSwapPPS.ask))),
  };

  const executeBid = () => {
    if (!canTrade || prices.isReadonly || !prices.isExecutable) {
      console.error('Cannot execute bid: either not tradeable, readonly or not executable');
      return;
    }

    if (
      isNotDefined(tile) ||
      isNotDefined(tile.properties.currency1) ||
      isNotDefined(tile.properties.currency2) ||
      isNotDefined(tile.properties.nearNotional) ||
      isNotDefined(tile.properties.nearNotionalCurrencyIndex) ||
      isNotDefined(tile.properties.nearExpiryDate) ||
      isNotDefined(tile.properties.farNotional) ||
      isNotDefined(tile.properties.farNotionalCurrencyIndex) ||
      isNotDefined(tile.properties.farExpiryDate) ||
      isNotDefined(clientId) ||
      isNotDefined(prices) ||
      isNotDefined(signalRConnectionId) ||
      isNotDefined(notificationEmail)
    ) {
      return;
    }

    const now = new Date();

    sgwtWebAnalytics?.trackEvent('Swap', 'RFS executed', 'bid');

    executeSwapRfs({
      tileId,
      rfsId: prices.rfsPrice.rfsId,
      quoteId: prices.rfsPrice.quote.quoteId,
      signalRConnectionId,
      notificationEmails: notificationEmail,
      clientId,

      isNonDeliverable: tile.isNonDeliverable,

      type: isSwapRollover(tile) ? 'rollover' : isSwapPredeliver(tile) ? 'predeliver' : 'standard',

      way: 'Bid',
      currency1: tile.properties.currency1,
      currency2: tile.properties.currency2,
      currencyPairPrecision: prices.currencyPairPrecision,

      swapPoints: prices.rates.bid,

      spot: prices.spot.bid,

      nearNotional: tile.properties.nearNotional,
      nearCurrencyIndex: tile.properties.nearNotionalCurrencyIndex,
      nearExpiryDate: tile.properties.nearExpiryDate,
      ...buildSwapExecutionPrices(prices, 'Bid'),

      farNotional: tile.properties.farNotional,
      farCurrencyIndex: tile.properties.farNotionalCurrencyIndex,
      farExpiryDate: tile.properties.farExpiryDate,

      userLocalDateTime: Date.now(),
      executionClickByFrontTimestamp: now.getTime(),

      lastQuoteFromBackTimestamp: prices.rfsPrice.quote.backendTimestamp,
      lastQuoteReceivedByFrontTimestamp: prices.rfsPrice.quote.frontendTimestamp,
    });
  };

  const executeAsk = () => {
    if (!canTrade || prices.isReadonly || !prices.isExecutable) {
      console.error('Cannot execute bid: either not tradeable, readonly or not executable');
      return;
    }

    if (
      isNotDefined(tile) ||
      isNotDefined(tile.properties.currency1) ||
      isNotDefined(tile.properties.currency2) ||
      isNotDefined(tile.properties.nearNotional) ||
      isNotDefined(tile.properties.nearNotionalCurrencyIndex) ||
      isNotDefined(tile.properties.nearExpiryDate) ||
      isNotDefined(tile.properties.farNotional) ||
      isNotDefined(tile.properties.farNotionalCurrencyIndex) ||
      isNotDefined(tile.properties.farExpiryDate) ||
      isNotDefined(clientId) ||
      isNotDefined(prices) ||
      isNotDefined(signalRConnectionId) ||
      isNotDefined(notificationEmail)
    ) {
      return;
    }

    const now = new Date();

    sgwtWebAnalytics?.trackEvent('Swap', 'RFS executed', 'ask');

    executeSwapRfs({
      tileId,
      rfsId: prices.rfsPrice.rfsId,
      quoteId: prices.rfsPrice.quote.quoteId,
      signalRConnectionId,
      notificationEmails: notificationEmail,
      clientId,

      isNonDeliverable: tile.isNonDeliverable,

      type: isSwapRollover(tile) ? 'rollover' : isSwapPredeliver(tile) ? 'predeliver' : 'standard',

      way: 'Ask',
      currency1: tile.properties.currency1,
      currency2: tile.properties.currency2,
      currencyPairPrecision: prices.currencyPairPrecision,

      swapPoints: prices.rates.ask,

      spot: prices.spot.ask,

      nearNotional: tile.properties.nearNotional,
      nearCurrencyIndex: tile.properties.nearNotionalCurrencyIndex,
      nearExpiryDate: tile.properties.nearExpiryDate,
      ...buildSwapExecutionPrices(prices, 'Ask'),

      farNotional: tile.properties.farNotional,
      farCurrencyIndex: tile.properties.farNotionalCurrencyIndex,
      farExpiryDate: tile.properties.farExpiryDate,

      userLocalDateTime: Date.now(),
      executionClickByFrontTimestamp: now.getTime(),

      lastQuoteFromBackTimestamp: prices.rfsPrice.quote.backendTimestamp,
      lastQuoteReceivedByFrontTimestamp: prices.rfsPrice.quote.frontendTimestamp,
    });
  };

  return (
    <HStack
      fullWidth
      className="position-relative"
      style={{
        height: 100,
      }}
      data-e2e="rates"
    >
      <div className="w-50 h-100 border-end text-center fw-semibold bg-lvl2" style={{ height: 100 }}>
        <ForbiddenExecutionTooltip placement="left">
          <VStack
            horizontalAlignment="start"
            verticalAlignment="full"
            fullWidth
            fullHeight
            className={clsx(
              'px-2 py-1',
              ...getRateComponentStyling({
                executable: prices.isExecutable,
                cantTrade: !canTrade || prices.isReadonly,
              }),
              { 'colored-prices': showColoredPrices },
              'bid',
            )}
            onDoubleClick={executeBid}
          >
            <div className="fs-12 line-height-1" data-e2e="left-rate-near-way">
              <FormattedMessage id={bidMessageId} values={{ currency, leg: 'near' }} />
            </div>
            <div className="fs-12 line-height-1" data-e2e="left-rate-far-way">
              <FormattedMessage id={askMessageId} values={{ currency, leg: 'far' }} />
            </div>

            <HStack verticalAlignment="end" fullWidth flex="expand" className="flex-fill fs-24 line-height-1 pb-2">
              <FormattedPrice
                value={rates.bid}
                precision={3}
                highlightDigitPosition={highlightDigitPositions.bid}
                data-e2e="left-rate"
                data-e2e-rate-type={prices.isExecutable ? 'executable' : 'indicative'}
                data-e2e-can-trade={canTrade}
                showColoredPrices={showColoredPrices}
              />
            </HStack>
          </VStack>
        </ForbiddenExecutionTooltip>
      </div>

      <div className="w-50 h-100 text-center fw-semibold bg-lvl2" style={{ height: 100 }}>
        <ForbiddenExecutionTooltip placement="right">
          <VStack
            horizontalAlignment="start"
            verticalAlignment="full"
            fullWidth
            fullHeight
            className={clsx(
              'px-2 py-1',
              { 'colored-prices': showColoredPrices },
              'ask',
              ...getRateComponentStyling({
                executable: prices.isExecutable,
                cantTrade: !canTrade || prices.isReadonly,
              }),
            )}
            onDoubleClick={executeAsk}
          >
            <div className="fs-12 line-height-1" data-e2e="right-rate-near-way">
              <FormattedMessage id={askMessageId} values={{ currency, leg: 'near' }} />
            </div>
            <div className="fs-12 line-height-1" data-e2e="right-rate-far-way">
              <FormattedMessage id={bidMessageId} values={{ currency, leg: 'far' }} />
            </div>

            <HStack verticalAlignment="end" fullWidth flex="expand" className="flex-fill fs-24 line-height-1 pb-2">
              <FormattedPrice
                value={rates.ask}
                precision={3}
                highlightDigitPosition={highlightDigitPositions.ask}
                data-e2e="right-rate"
                data-e2e-rate-type={prices.isExecutable ? 'executable' : 'indicative'}
                data-e2e-can-trade={canTrade}
                showColoredPrices={showColoredPrices}
              />
            </HStack>
          </VStack>
        </ForbiddenExecutionTooltip>
      </div>

      {isSaleTier && <SaleTier />}
    </HStack>
  );
};
